export const ACTIVITY_TITLE_MAX_LENGTH = 500;

export interface ProvenanceFieldState {
  value: string;
  lastGenerated: string;
  dirty: boolean;
}

export interface ActivityBoardCardPrefill {
  provider: string;
  externalId: string;
  title: string;
  context: string;
}

export interface ActivityCreationContentState {
  title: ProvenanceFieldState;
  description: ProvenanceFieldState;
  card: ActivityBoardCardPrefill | null;
}

export type ActivityCreationContentAction =
  | { type: 'title.edited'; value: string }
  | { type: 'description.edited'; value: string }
  | { type: 'card.selected'; card: ActivityBoardCardPrefill }
  | { type: 'card.removed' }
  | { type: 'form.reset' };

const emptyField = (): ProvenanceFieldState => ({
  value: '',
  lastGenerated: '',
  dirty: false,
});

export const createInitialActivityCreationContent =
  (): ActivityCreationContentState => ({
    title: emptyField(),
    description: emptyField(),
    card: null,
  });

export const truncateGeneratedTitle = (value: string) =>
  value.slice(0, ACTIVITY_TITLE_MAX_LENGTH);

export const validateActivityTitle = (value: string): string | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return 'Título é obrigatório';
  if (trimmed.length > ACTIVITY_TITLE_MAX_LENGTH) {
    return `Título deve ter no máximo ${ACTIVITY_TITLE_MAX_LENGTH} caracteres`;
  }
  return undefined;
};

const selectCard = (
  state: ActivityCreationContentState,
  card: ActivityBoardCardPrefill,
): ActivityCreationContentState => {
  const generatedTitle = truncateGeneratedTitle(card.title);
  return {
    card,
    title: {
      value: state.title.dirty ? state.title.value : generatedTitle,
      lastGenerated: generatedTitle,
      dirty: state.title.dirty,
    },
    description: {
      ...state.description,
      lastGenerated: card.context,
    },
  };
};

const removeCard = (
  state: ActivityCreationContentState,
): ActivityCreationContentState => ({
  card: null,
  title: {
    value: state.title.dirty ? state.title.value : '',
    lastGenerated: '',
    dirty: state.title.dirty,
  },
  description: {
    ...state.description,
    lastGenerated: '',
  },
});

export const activityCreationContentReducer = (
  state: ActivityCreationContentState,
  action: ActivityCreationContentAction,
): ActivityCreationContentState => {
  switch (action.type) {
    case 'title.edited':
      return {
        ...state,
        title: { ...state.title, value: action.value, dirty: true },
      };
    case 'description.edited':
      return {
        ...state,
        description: { ...state.description, value: action.value, dirty: true },
      };
    case 'card.selected':
      return selectCard(state, action.card);
    case 'card.removed':
      return removeCard(state);
    case 'form.reset':
      return createInitialActivityCreationContent();
    default:
      return state;
  }
};

export const composeActivityDescription = (
  manualValue: string,
  generatedContext: string,
): string | undefined => {
  const manual = manualValue.trim();
  const context = generatedContext.trim();
  if (!manual && !context) return undefined;
  if (!context) return manual;
  const imported = `## Contexto importado do card\n\n${context}`;
  return manual ? `${manual}\n\n${imported}` : imported;
};
